import React from 'react';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import { Button, Checkbox, Input, Modal } from 'ui';
import useQueryApiClient from 'utils/useQueryApiClient';

interface AddCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (farmId: number) => void;
}

interface AddCompanyFormValues {
  reg_nr: string;
  is_svd?: boolean;
  name?: string;
  surname?: string;
  pvd_nr?: string;
}

export const AddCompanyModal = ({
  open,
  onClose,
  onCreated,
}: AddCompanyModalProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const isSvd = !!Form.useWatch('is_svd', form);

  const { appendData, isLoading } = useQueryApiClient({
    request: {
      url: 'api/v2/farms',
      method: 'POST',
    },
    onSuccess: (response) => {
      handleClose();
      onCreated(response.id);
    },
    onError: (response) => {
      const errorKey: string = response?.error ?? '';

      form.setFields([
        {
          name: 'reg_nr',
          errors: [
            intl.formatMessage({
              id: errorKey.startsWith('farms.add_company.')
                ? errorKey
                : 'farms.add_company.lookup_failed',
            }),
          ],
        },
      ]);
    },
  });

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = (values: AddCompanyFormValues) => {
    appendData({
      reg_nr: values.reg_nr.trim(),
      is_svd: isSvd,
      name: (isSvd && values.name?.trim()) || undefined,
      surname: (isSvd && values.surname?.trim()) || undefined,
      pvd_nr: values.pvd_nr?.trim() || undefined,
    });
  };

  return (
    <Modal
      title={intl.formatMessage({ id: 'farms.add_farm' })}
      open={open}
      onCancel={() => !isLoading && handleClose()}
      closable={!isLoading}
      destroyOnClose
      footer={
        <>
          <Button
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={handleClose}
            disabled={isLoading}
          />
          <Button
            label={intl.formatMessage({ id: 'general.add' })}
            onClick={() => form.submit()}
            type="primary"
            loading={isLoading}
          />
        </>
      }
    >
      <Form form={form} onFinish={handleFinish} layout="vertical">
        <Checkbox
          name="is_svd"
          label={intl.formatMessage({ id: 'farms.add_company.is_svd' })}
          disabled={isLoading}
        />
        <Input
          name="reg_nr"
          label={intl.formatMessage({
            id: isSvd ? 'farms.add_company.personal_code' : 'farms.reg_nr',
          })}
          validations={['required', isSvd ? 'personalCode' : 'regNr']}
          maxLength={11}
          disabled={isLoading}
        />
        {isSvd && (
          <>
            <Input
              name="name"
              label={intl.formatMessage({ id: 'general.first_name' })}
              validations="required"
              disabled={isLoading}
            />
            <Input
              name="surname"
              label={intl.formatMessage({ id: 'general.surname' })}
              validations="required"
              disabled={isLoading}
            />
          </>
        )}
        <Input
          name="pvd_nr"
          label={intl.formatMessage({ id: 'farms.pvd_nr' })}
          validations="pvdNumber"
          maxLength={6}
          disabled={isLoading}
        />
        <p>
          {intl.formatMessage({
            id: isLoading
              ? 'farms.add_company.loading'
              : 'farms.add_company.hint',
          })}
        </p>
      </Form>
    </Modal>
  );
};
